/**
 * WB3-5 — Explanatory AI layer ("Why?" on disabled actions; fallback to static reason).
 */

import { useCallback, useState } from 'react';
import { Button, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { AssistiveEntityContext } from './build-assistive-payload';
import type { WorkbenchFocus } from '@shared/business-workbench/workbench-focus';
import { useAssistiveAI } from '@frontend/domain-hooks/conscious/useAssistiveAI';
import { buildExplainSourceText } from './build-assistive-payload';
import { css, omitUndefined } from '@shared/utils/omitUndefined';
import styles from './ExplanatoryInline.module.css';

export interface ExplanatoryInlineProps {
  tenantId: string;
  actionId: string;
  actionLabel?: string;
  staticReason?: string;
  focus?: WorkbenchFocus;
  entity?: AssistiveEntityContext;
  testId?: string;
}

export function ExplanatoryInline({
  tenantId,
  actionId,
  actionLabel,
  staticReason,
  focus,
  entity,
  testId = 'ai-explanatory-inline',
}: ExplanatoryInlineProps) {
  const { loading, summarize } = useAssistiveAI(tenantId);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const message = staticReason?.trim() || '이 작업을 수행할 권한이 없습니다.';

  const handleWhy = useCallback(async () => {
    setExpanded(true);
    const result = await summarize(
      buildExplainSourceText(
        omitUndefined({
          staticReason: message,
          actionId,
          actionLabel,
          focus,
          entity,
        })
      )
    );
    setAiExplanation(result?.summary ?? message);
  }, [actionId, actionLabel, entity, focus, message, summarize]);

  return (
    <div role="status" aria-live="polite" className={styles.root} data-testid={testId}>
      <p className={styles.reason}>
        <InfoCircleOutlined aria-hidden />
        {message}
      </p>
      <Button
        type="link"
        size="small"
        className={css(styles.whyButton)}
        onClick={() => void handleWhy()}
        data-testid={`${testId}-why`}
      >
        Why?
      </Button>
      {expanded ? (
        loading ? (
          <Spin size="small" data-testid={`${testId}-loading`} />
        ) : (
          <p className={styles.explanation} data-testid={`${testId}-explanation`}>
            {aiExplanation ?? message}
          </p>
        )
      ) : null}
    </div>
  );
}
